import PropTypes from "prop-types";
import { forwardRef, useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress } from "@mui/material";

// The Safrati button standard (docs/ui-standard.md §3), in MUI, for the CMS:
//   contained = primary (one per view), outlined = secondary, text = quiet,
//   tone="destructive" = terracotta text, always behind an in-app confirm.
// Pill shaped; small 40px for inline actions, medium 48px for a dialog's
// commit; 44px on touch. A press pops out (never sinks), focus shows a 2px
// ring, a disabled button explains itself nearby (the caller's job).
// Busy: when onClick returns a promise (or `busy` is set) the button shows a
// spinner and its busy label at the SAME width, is aria-busy, and ignores
// further presses until the work settles.

const SIZES = {
  small: { height: 40, px: 2, quietPx: 1.5, font: "0.875rem", icon: 16 },
  medium: { height: 48, px: 3, quietPx: 2, font: "1rem", icon: 18 },
  large: { height: 52, px: 3.5, quietPx: 2.5, font: "1.0625rem", icon: 18 },
};

export function actionButtonSx(variant = "outlined", size = "small", tone) {
  return (theme) => {
    const s = SIZES[size] || SIZES.small;
    const dark = theme.palette.mode === "dark";
    const brand = theme.palette.primary;
    const accent = theme.palette.secondary;
    const line = dark ? "rgba(255, 255, 255, 0.24)" : "#C8D2DF";
    const variants = {
      contained: {
        backgroundColor: brand.main,
        color: brand.contrastText,
        boxShadow: "0 4px 12px rgba(0, 74, 173, 0.22)",
        "&:hover": { backgroundColor: brand.dark, boxShadow: "0 4px 12px rgba(0, 74, 173, 0.22)" },
      },
      outlined: {
        backgroundColor: theme.palette.background.paper,
        color: brand.main,
        border: `1px solid ${line}`,
        "&:hover": { backgroundColor: brand.light, borderColor: brand.main },
      },
      text: {
        backgroundColor: "transparent",
        color: brand.main,
        px: s.quietPx,
        "&:hover": { backgroundColor: brand.light },
      },
    };
    const destructive = {
      backgroundColor: "transparent",
      border: 0,
      boxShadow: "none",
      color: dark ? accent.main : accent.dark,
      "&:hover": { backgroundColor: accent.light },
    };
    return {
      minHeight: s.height,
      px: s.px,
      py: 0,
      borderRadius: 999,
      fontSize: s.font,
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: 0,
      textTransform: "none",
      whiteSpace: "nowrap",
      transition: `${theme.transitions.create(["background-color", "border-color", "color", "box-shadow"], {
        duration: 140,
      })}, transform 60ms ease-out`,
      ...variants[variant],
      ...(tone === "destructive" ? destructive : {}),
      "&:active": { transform: "scale(1.03)" },
      "@media (prefers-reduced-motion: reduce)": { "&:active": { transform: "none" } },
      "&.Mui-focusVisible, &:focus-visible": {
        outline: `2px solid ${dark ? "#ffffff" : brand.main}`,
        outlineOffset: "2px",
      },
      "&.Mui-disabled": {
        opacity: 0.5,
        boxShadow: "none",
        cursor: "not-allowed",
        pointerEvents: "auto",
        color: variants[variant].color,
        backgroundColor: variants[variant].backgroundColor,
        ...(tone === "destructive" ? { color: destructive.color, backgroundColor: "transparent" } : {}),
      },
      "& .MuiButton-startIcon": { ml: 0, mr: 1 },
      "& .MuiButton-endIcon": { ml: 1, mr: 0 },
      "@media (pointer: coarse)": { minHeight: Math.max(44, s.height) },
    };
  };
}

const ActionButton = forwardRef(function ActionButton(
  { variant = "outlined", size = "small", tone, busy: busyProp = false, busyLabel, startIcon, endIcon, onClick, children, sx, ...rest },
  ref,
) {
  const [own, setOwn] = useState(false);
  const running = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const busy = !!busyProp || own;
  const iconSize = (SIZES[size] || SIZES.small).icon;
  const spinner = <CircularProgress size={iconSize} thickness={5} color="inherit" />;

  const handleClick = (event) => {
    if (busy || running.current) {
      event.preventDefault();
      return;
    }
    const result = onClick?.(event);
    if (result && typeof result.then === "function") {
      running.current = true;
      setOwn(true);
      result
        .catch(() => {
          /* the caller says what went wrong */
        })
        .finally(() => {
          running.current = false;
          if (mounted.current) setOwn(false);
        });
    }
  };

  // Both labels sit in one grid cell, so the button is as wide as the longer
  // of the two whether busy or not: no jump when the label changes.
  const label = (
    <Box component="span" sx={{ display: "inline-grid", alignItems: "center" }}>
      <Box component="span" sx={{ gridArea: "1 / 1", visibility: busy ? "hidden" : "visible" }}>
        {children}
      </Box>
      <Box
        component="span"
        sx={{ gridArea: "1 / 1", visibility: busy ? "visible" : "hidden", display: "inline-flex", alignItems: "center", gap: 1 }}
      >
        {!startIcon && spinner}
        {busyLabel || children}
      </Box>
    </Box>
  );

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      disableRipple
      disableElevation
      aria-busy={busy || undefined}
      startIcon={startIcon ? (busy ? spinner : startIcon) : undefined}
      endIcon={endIcon}
      onClick={handleClick}
      sx={[actionButtonSx(variant, size, tone), ...(Array.isArray(sx) ? sx : [sx])]}
      {...rest}
    >
      {label}
    </Button>
  );
});

ActionButton.propTypes = {
  variant: PropTypes.oneOf(["contained", "outlined", "text"]),
  size: PropTypes.oneOf(["small", "medium", "large"]),
  tone: PropTypes.oneOf(["destructive"]),
  busy: PropTypes.bool,
  busyLabel: PropTypes.node,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  onClick: PropTypes.func,
  children: PropTypes.node,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.func, PropTypes.array]),
};

export default ActionButton;

// Focus ring and touch target for the workspace's icon-only buttons (copy,
// previous / next on a phone): the visual stays small, the hit area is 44px.
export const iconButtonSx = (theme) => ({
  position: "relative",
  "&::after": { content: '""', position: "absolute", inset: -6 },
  "&.Mui-focusVisible, &:focus-visible": {
    outline: `2px solid ${theme.palette.mode === "dark" ? "#ffffff" : theme.palette.primary.main}`,
    outlineOffset: "2px",
  },
});
